import { ReactNode, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { LoaderIcon, ShieldCheck } from "lucide-react"

import BellIcon from "@/assets/icons/bell/regular.svg?react"
import LogoutIcon from "@/assets/icons/logout/regular.svg?react"
import ProfileIcon from "@/assets/icons/profile/regular.svg?react"
import ProfitIcon from "@/assets/icons/profit/regular.svg?react"
import QuestionIcon from "@/assets/icons/question/regular.svg?react"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ds/Popover"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { SheetContent, SheetRoot } from "@/components/ui/sheet"
import Toast from "@/components/ui/toast"
import { useUser } from "@/hooks/useUser"
import { cn } from "@/lib/utils"
import router from "@/routes/router"
import routes from "@/routes/routes"
import { useLogoutMutation } from "@/store/authAPI"
import { IUser } from "@/store/user/userSlice"

import ProfileQuickView from "../Navbar/ProfileQuickView"

function MenuItem({
  href,
  icon,
  children,
  onClick,
  disabled,
  variant = "default",
}: {
  href?: string
  icon: ReactNode
  children: ReactNode
  onClick?: () => void
  disabled?: boolean
  variant?: "default" | "danger"
}) {
  const className = cn(
    "flex w-full items-center gap-3 px-6 py-3 text-left text-body2 text-neutral-text transition-colors",
    variant === "danger"
      ? "hover:bg-danger-secondBg hover:text-danger-primary"
      : "hover:bg-neutral-secondBg hover:text-neutral-title",
  )

  if (href) {
    return (
      <Link to={href} onClick={onClick} className={className}>
        {icon} {children}
      </Link>
    )
  }

  return (
    <button type="button" onClick={onClick} disabled={disabled} className={className}>
      {icon} {children}
    </button>
  )
}

const generateAvatarFallbackText = (user: IUser | null) => {
  if (!user?.firstName && !user?.lastName) return "N/A"
  return `${user.firstName || ""} ${user.lastName || ""}`
    .trim()
    .split(" ")
    .map((part) => part[0]?.toUpperCase())
    .slice(0, 2)
    .join("")
}

export default function HeaderProfileMenu() {
  const { t } = useTranslation()
  const { user } = useUser()
  const [popoverOpen, setPopoverOpen] = useState(false)
  const [profileOpen, setProfileOpen] = useState(false)
  const [logout, { isLoading }] = useLogoutMutation()
  const { logout: clearUser } = useUser()

  const handleProfileClick = () => {
    setPopoverOpen(false)
    setProfileOpen(true)
  }

  const handleLogout = async () => {
    try {
      const { success, error } = await logout().unwrap()

      if (success == true) {
        clearUser()
        router.navigate("/login")
      } else {
        Toast({ type: "error", message: error as string })
      }
    } catch {
      Toast({ type: "error", message: "Something went wrong." })
    }
  }

  return (
    <>
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger
          className="flex items-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
          aria-label={t("profile")}
        >
          <Avatar className="h-10 w-10 cursor-pointer transition-opacity hover:opacity-80">
            <AvatarImage src={user.image ?? ""} alt={t("profile")} />
            <AvatarFallback className="bg-neutral-border text-sm font-medium text-neutral-title">
              {generateAvatarFallbackText(user)}
            </AvatarFallback>
          </Avatar>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          side="bottom"
          sideOffset={12}
          className="w-72 overflow-hidden rounded-2xl bg-white px-0 py-2 shadow-[0_-10px_24px_0_rgba(6,15,51,0.02),0_10px_24px_0_rgba(6,15,51,0.04)]"
        >
          {/* Identity section */}
          <div className="flex gap-3 border-b px-6 py-4">
            <Avatar className="h-10 w-10 shrink-0">
              <AvatarImage src={user.image ?? ""} alt={t("profile")} />
              <AvatarFallback className="bg-neutral-border text-sm font-medium text-neutral-title">
                {generateAvatarFallbackText(user)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <span className="block truncate text-body2 font-medium text-neutral-title">
                {user.firstName} {user.lastName}
              </span>
              <span className="block truncate text-body2 text-neutral-text">{user?.email}</span>
            </div>
          </div>

          {/* Actions */}
          <div className="py-1">
            <MenuItem onClick={handleProfileClick} icon={<ProfileIcon className="h-5 w-5" />}>
              {t("profile")}
            </MenuItem>
            <MenuItem
              href={routes["dashboard/wallet"]}
              onClick={() => setPopoverOpen(false)}
              icon={<ProfitIcon className="h-5 w-5" />}
            >
              {t("wallet.navTitle")}
            </MenuItem>
            <MenuItem
              href={routes["dashboard/settings/notifications"]}
              onClick={() => setPopoverOpen(false)}
              icon={<BellIcon className="h-5 w-5" />}
            >
              {t("manageNotifications")}
            </MenuItem>
            <MenuItem
              href={routes["dashboard/settings/security"]}
              onClick={() => setPopoverOpen(false)}
              icon={<ShieldCheck className="h-5 w-5" />}
            >
              {t("manageSecurity")}
            </MenuItem>
            <MenuItem href="/TODO" onClick={() => setPopoverOpen(false)} icon={<QuestionIcon className="h-5 w-5" />}>
              {t("contactSupport")}
            </MenuItem>
          </div>

          {/* Logout */}
          <div className="border-t py-1">
            <MenuItem
              onClick={() => handleLogout()}
              disabled={isLoading}
              variant="danger"
              icon={isLoading ? <LoaderIcon className="h-4 w-4 animate-spin" /> : <LogoutIcon className="h-5 w-5" />}
            >
              {t("logOut")}
            </MenuItem>
          </div>
        </PopoverContent>
      </Popover>

      {/* Profile Sheet */}
      <SheetRoot open={profileOpen} onOpenChange={setProfileOpen}>
        <SheetContent>
          <ProfileQuickView />
        </SheetContent>
      </SheetRoot>
    </>
  )
}
